import {
  TypeLayer,
  TypeRow,
  TypeVendor,
  TypeVendorInfo,
  TypeWorkingBudgetInfo
} from '../types/WorkingBudgetPage';
import { LAYER_LIST } from '../const/WorkingBudgetPage';
import {
  deleteWorkingBudgetSelectAdditionalCost,
  deleteWorkingBudgetVender,
  getWorkingBudget,
  updateTotallingInfo,
  updateWorkingBudget
} from '../apicalls/WorkingBudgetPage';
import { NOT_LOGGED_IN } from '../const/Const';
import _ from 'lodash';
import { calcTotalFromQuantityAndUnitPrice } from './calcRules';

export const recalculation = (
  layerData: TypeLayer,
  vender: TypeVendorInfo[],
  setWorkingBudget: (newWorkingBudget: TypeLayer | undefined) => void,
  setVendor: (newVendor: TypeVendorInfo[] | undefined) => void,
  additionalCosts?: TypeRow[]
) => {
  // 階層のリストを作成
  const layers: string[] = [
    LAYER_LIST.FIFTH,
    LAYER_LIST.FOURTH,
    LAYER_LIST.THIRD,
    LAYER_LIST.SECOND,
    LAYER_LIST.FIRST
  ];

  // 各階層での集計を行う関数
  const aggregateOrderPrice = (
    currentLayer: TypeRow[],
    currentLayerName: string
  ) => {
    currentLayer.forEach((item: TypeRow) => {
      if (item.has_under_layer_flg) {
        let childLayer =
          layerData[layers[layers.indexOf(currentLayerName) - 1]];
        let children = childLayer.filter(
          child => child.parent_item_id === item.id
        );

        // Reset the vendor_order_price of the parent item
        item.vendor_order_price = [];

        children.forEach(child => {
          child.vendor_order_price.forEach(childVop => {
            let vopIndex = item.vendor_order_price.findIndex(
              (v: TypeVendor) => v.vendor_id === childVop.vendor_id
            );
            if (vopIndex !== -1) {
              item.vendor_order_price[vopIndex].order_price +=
                childVop.order_price;
            } else {
              item.vendor_order_price.push({
                id: -1,
                vendor_id: childVop.vendor_id,
                order_price: childVop.order_price,
                target_item_id: item.id ? item.id : 0
              });
            }
          });
        });
      }
    });
  };

  // 各ベンダーの集計結果をvenderListのtotalに反映する関数
  const aggregateVendorTotal = (
    venderList: TypeVendorInfo[],
    layer: TypeRow[]
  ) => {
    venderList.forEach(vendor => {
      vendor.total = 0; // まず各ベンダーのtotalをリセット
      vendor.additional_cost = 0;
      layer.forEach(item => {
        item.vendor_order_price.forEach(vop => {
          if (vop.vendor_id === vendor.vendor_id) {
            vendor.total += vop.order_price;
          }
        });
      });

      if (additionalCosts) {
        additionalCosts.forEach(item => {
          item.vendor_order_price.forEach(vop => {
            if (vop.vendor_id === vendor.vendor_id) {
              vendor.additional_cost += vop.order_price;
            }
          });
        });
      }
    });
  };

  // 各階層で上記の関数を実行
  layers.forEach(currentLayerName => {
    aggregateOrderPrice(layerData[currentLayerName], currentLayerName);
  });

  aggregateVendorTotal(vender, layerData[LAYER_LIST.FIRST]);

  setWorkingBudget(layerData);
  setVendor(vender);
};

/**
 * 登録時のバリデーション
 * @param workingBudget
 * @param additionalCost
 */
export const checkReqData = async (
  workingBudget: TypeLayer,
  additionalCost: TypeRow[]
) => {
  let errorMessagesArray: string[] = [];

  // 見積書からのデータをチェック
  for (const layerKey in workingBudget) {
    if (Object.prototype.hasOwnProperty.call(workingBudget, layerKey)) {
      workingBudget[layerKey].forEach((item: TypeRow, index: number) => {
        // 既存の金額チェック
        item.vendor_order_price.forEach(vendorOrder => {
          if (!isHalfWidthNumber(vendorOrder.order_price)) {
            errorMessagesArray.push(
              `「${
                item.name ||
                `名称未設定アイテム (${layerKey} - ${index + 1}番目)`
              }」に正しい金額を入力してください。`
            );
          }
        });

        const quantity = item.quantity;
        // selling_price が 0 でない場合に quantity が 0 や null/undefined/空文字 は通常ありえない、という前提
        if (
          item.selling_price !== 0 &&
          (quantity === null ||
            quantity === undefined ||
            (typeof quantity === 'string' && quantity === '') ||
            Number(quantity) === 0)
        ) {
          errorMessagesArray.push(
            `「${
              item.name || `名称未設定アイテム (${layerKey} - ${index + 1}番目)`
            }」の数量は必須です。`
          );
        } else if (
          quantity === null ||
          quantity === undefined ||
          (typeof quantity === 'string' && quantity === '')
        ) {
          errorMessagesArray.push(
            `「${
              item.name || `名称未設定アイテム (${layerKey} - ${index + 1}番目)`
            }」の数量を入力してください（値がありません）。`
          );
        }
      });
    }
  }

  return errorMessagesArray.length > 0 ? errorMessagesArray.join('\n') : '';
};

const isHalfWidthNumber = (value: any) => {
  return /^[\d]+$/.test(value.toString());
};

/**
 * 実行予算詳細取得
 */
export const getWorkingBudgetData = async (
  workingBudgetId: number,
  changeLoggedInData: (newData: string) => void,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setWorkingBudget: (newWorkingBudget: TypeLayer | undefined) => void,
  setVendor: (newVendor: TypeVendorInfo[] | undefined) => void,
  setAdditionalCost: (newAdditionalCost: TypeRow[]) => void
) => {
  return await getWorkingBudget(workingBudgetId).then(async data => {
    if (data.data === NOT_LOGGED_IN) {
      changeLoggedInData(NOT_LOGGED_IN);
    } else {
      if (typeof data.data === 'string') {
        setErrorMessage(data.data);
        setOpen(true);
      } else {
        return await setDrawingData(
          data,
          setWorkingBudget,
          setVendor,
          setAdditionalCost
        );
      }
    }
  });
};

/**
 * 実行予算詳細取得(データ返却版)
 */
export const getReturnWorkingBudgetData = async (
  workingBudgetId: number,
  changeLoggedInData: (newData: string) => void,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setWorkingBudget: (newWorkingBudget: TypeLayer | undefined) => void,
  setVendor: (newVendor: TypeVendorInfo[] | undefined) => void,
  setAdditionalCost: (newAdditionalCost: TypeRow[]) => void
) => {
  return await getWorkingBudget(workingBudgetId).then(async data => {
    if (data.data === NOT_LOGGED_IN) {
      changeLoggedInData(NOT_LOGGED_IN);
    } else {
      if (typeof data.data === 'string') {
        setErrorMessage(data.data);
        setOpen(true);
      } else {
        return data;
      }
    }
  });
};

/**
 * 保存処理
 */
export const updateWorkingBudgetData = async (
  workingBudget: TypeLayer,
  additionalCost: TypeRow[],
  vendor: TypeVendorInfo[] | undefined,
  totallingInfo: TypeWorkingBudgetInfo | undefined,
  workingBudgetId: number,
  changeLoggedInData: (newData: string) => void,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>,
  setOpenSuccess: React.Dispatch<React.SetStateAction<boolean>>,
  setWorkingBudget: (newWorkingBudget: TypeLayer | undefined) => void,
  setVendor: (newVendor: TypeVendorInfo[] | undefined) => void,
  setAdditionalCost: (newAdditionalCost: TypeRow[]) => void,
  returnFlg: boolean
) => {
  return await checkReqData(workingBudget, additionalCost).then(
    async errorMessages => {
      if (errorMessages) {
        setOpen(true);
        setErrorMessage(errorMessages);
      } else {
        const reqData = {
          working_budget: {
            first_layer: workingBudget['layer1'],
            second_layer: workingBudget['layer2'],
            third_layer: workingBudget['layer3'],
            fourth_layer: workingBudget['layer4'],
            fifth_layer: workingBudget['layer5']
          },
          vender: vendor,
          additional_cost: additionalCost,
          totalling_info: totallingInfo
        };
        return await updateWorkingBudget(reqData, workingBudgetId).then(
          async data => {
            if (data.data === NOT_LOGGED_IN) {
              changeLoggedInData(NOT_LOGGED_IN);
            } else {
              if (typeof data.data === 'string') {
                setErrorMessage(data.data);
                setOpen(true);
              } else {
                if (returnFlg) {
                  return data;
                } else {
                  setSuccessMessage(data.message);
                  setOpenSuccess(true);
                  return await setDrawingData(
                    data,
                    setWorkingBudget,
                    setVendor,
                    setAdditionalCost
                  );
                }
              }
            }
          }
        );
      }
    }
  );
};

/**
 * 描画用データ設定
 */
export const setDrawingData = async (
  data: any,
  setWorkingBudget: (newWorkingBudget: TypeLayer | undefined) => void,
  setVendor: (newVendor: TypeVendorInfo[] | undefined) => void,
  setAdditionalCost: (newAdditionalCost: TypeRow[]) => void
) => {
  const layerData: TypeLayer = {
    [LAYER_LIST.FIRST]: data.data.list.first_layer,
    [LAYER_LIST.SECOND]: data.data.list.second_layer,
    [LAYER_LIST.THIRD]: data.data.list.third_layer,
    [LAYER_LIST.FOURTH]: data.data.list.fourth_layer,
    [LAYER_LIST.FIFTH]: data.data.list.fifth_layer
  };

  const vender: TypeVendorInfo[] = data.data.venderList;

  // 一覧の計算
  recalculation(
    layerData,
    vender,
    setWorkingBudget,
    setVendor,
    data.data.additional_costs
  );
  // 追加原価の設定
  setAdditionalCost(data.data.additional_costs);
};

export const deleteWorkingBudgetVenderData = async (
  filteredData: TypeVendorInfo,
  workingBudgetId: number,
  changeLoggedInData: (newData: string) => void,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>,
  setOpenSuccess: React.Dispatch<React.SetStateAction<boolean>>,
  setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>,
  setWorkingBudget: (newWorkingBudget: TypeLayer | undefined) => void,
  setVendor: (newVendor: TypeVendorInfo[] | undefined) => void,
  setAdditionalCost: (newAdditionalCost: TypeRow[]) => void,
  cloneTotallingInfo: TypeWorkingBudgetInfo
) => {
  return await deleteWorkingBudgetVender(filteredData, workingBudgetId).then(
    data => {
      if (data.data === NOT_LOGGED_IN) {
        changeLoggedInData(NOT_LOGGED_IN);
      } else {
        if (typeof data.data === 'string') {
          setErrorMessage(data.data);
          setOpen(true);
        } else {
          const vender: TypeVendorInfo[] = data.data.venderList;
          let total = 0;
          vender.map((v: TypeVendorInfo) => {
            total += v.total + v.additional_cost;
          });
          const reqData: TypeWorkingBudgetInfo = {
            additional_cost: cloneTotallingInfo.additional_cost,
            buffer:
              cloneTotallingInfo.estimate_cost_total +
              cloneTotallingInfo.additional_cost -
              total,
            estimate_cost_total: cloneTotallingInfo.estimate_cost_total,
            estimate_total: cloneTotallingInfo.estimate_total,
            final_gross_profit: cloneTotallingInfo.final_gross_profit,
            final_gross_profit_margin:
              cloneTotallingInfo.final_gross_profit_margin,
            gross_profit: cloneTotallingInfo.gross_profit,
            gross_profit_margin: cloneTotallingInfo.gross_profit_margin,
            order_total: total
          };
          // 集計エリアの保存
          updateTotallingInfo(reqData, workingBudgetId).then(
            async updateTotallingInfoData => {
              if (updateTotallingInfoData.data === NOT_LOGGED_IN) {
                changeLoggedInData(NOT_LOGGED_IN);
              } else {
                await setDrawingData(
                  data,
                  setWorkingBudget,
                  setVendor,
                  setAdditionalCost
                );
                setSuccessMessage('業者の削除に成功しました。');
                setOpenSuccess(true);
                setOpenDeleteModal(false);
                return;
              }
            }
          );
        }
      }
    }
  );
};

export const deleteWorkingBudgetSelectAdditionalCostData = async (
  deleteItems: TypeRow[],
  workingBudgetId: number,
  changeLoggedInData: (newData: string) => void,
  totallingInfo: TypeWorkingBudgetInfo,
  setWorkingBudget: (newWorkingBudget: TypeLayer | undefined) => void,
  setVendor: (newVendor: TypeVendorInfo[] | undefined) => void,
  setAdditionalCost: (newAdditionalCost: TypeRow[]) => void,
  setOpenSuccess: React.Dispatch<React.SetStateAction<boolean>>,
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>
) => {
  return await deleteWorkingBudgetSelectAdditionalCost(
    deleteItems,
    workingBudgetId
  ).then(async data => {
    if (data.data === NOT_LOGGED_IN) {
      changeLoggedInData(NOT_LOGGED_IN);
    } else {
      const resAdditionalCost: TypeRow[] = data.data.additional_costs;
      const vender: TypeVendorInfo[] = data.data.venderList;
      let additionalCostTotal = 0;
      resAdditionalCost.map(a => {
        additionalCostTotal += a.cost_unit_price * a.quantity;
      });
      let venderTotal = 0;
      vender.map((v: TypeVendorInfo) => {
        venderTotal += v.total + v.additional_cost;
      });

      const reqData: TypeWorkingBudgetInfo = {
        additional_cost: additionalCostTotal,
        buffer:
          totallingInfo.estimate_cost_total +
          totallingInfo.additional_cost -
          venderTotal,
        estimate_cost_total: totallingInfo.estimate_cost_total,
        estimate_total: totallingInfo.estimate_total,
        final_gross_profit: totallingInfo.gross_profit - additionalCostTotal,
        final_gross_profit_margin:
          totallingInfo.estimate_total > 0
            ? Math.floor(
                ((totallingInfo.gross_profit - additionalCostTotal) /
                  totallingInfo.estimate_total) *
                  100
              )
            : 0,
        gross_profit: totallingInfo.gross_profit,
        gross_profit_margin: totallingInfo.gross_profit_margin,
        order_total: venderTotal
      };
      return await updateTotallingInfo(reqData, workingBudgetId).then(
        async updateTotallingInfoData => {
          if (updateTotallingInfoData.data === NOT_LOGGED_IN) {
            changeLoggedInData(NOT_LOGGED_IN);
          } else {
            await setDrawingData(
              data,
              setWorkingBudget,
              setVendor,
              setAdditionalCost
            );
            setOpenSuccess(true);
            setSuccessMessage('選択行の削除に成功しました。');
            return;
          }
        }
      );
    }
  });
};

/**
 * 集計エリアの計算
 */
export const calculationTotallingInfo = (
  vendor: TypeVendorInfo[],
  additionalCost: TypeRow[],
  cloneTotallingInfo: TypeWorkingBudgetInfo,
  setTotallingInfo: any
) => {
  let vendorTotal = 0;
  vendor.map(v => {
    vendorTotal += v.total + v.additional_cost;
  });
  let additionalCostTotal = 0;
  additionalCost.map(a => {
    additionalCostTotal += calcTotalFromQuantityAndUnitPrice(
      a.quantity,
      a.cost_unit_price
    );
  });
  cloneTotallingInfo.order_total = vendorTotal;
  cloneTotallingInfo.additional_cost = additionalCostTotal;
  cloneTotallingInfo.final_gross_profit =
    cloneTotallingInfo.gross_profit - additionalCostTotal;
  cloneTotallingInfo.final_gross_profit_margin =
    cloneTotallingInfo.estimate_total > 0
      ? Math.floor(
          ((cloneTotallingInfo.gross_profit - additionalCostTotal) /
            cloneTotallingInfo.estimate_total) *
            100
        )
      : 0;
  cloneTotallingInfo.buffer =
    cloneTotallingInfo.estimate_cost_total +
    cloneTotallingInfo.additional_cost -
    cloneTotallingInfo.order_total;
  setTotallingInfo(cloneTotallingInfo);
};
